import React, { FC, Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Checkbox, Grid, Typography } from '@mui/material';
import TimeMinute from 'components/orders/shared/time-minute.component';
import { LabSettings, Organization } from 'models/organization';

type LabOpeningHoursProps = {
    onDayTimesChange: (dayTimes: LabSettings) => void;
    currentLab: Organization;
};

const LabOpeningHours: FC<LabOpeningHoursProps> = ({ onDayTimesChange, currentLab }: LabOpeningHoursProps) => {
    const { t: translate } = useTranslation(['lab-settings']);
    const [dayTimes, setDayTimes] = useState<LabSettings>({});

    const toggleDay = (day: string) => {
        setDayTimes((prevState) => ({
            ...prevState,
            [day]: {
                ...prevState[day],
                active: !prevState[day].active,
            },
        }));

        onDayTimesChange({ ...dayTimes, [day]: { ...dayTimes[day], active: !dayTimes[day].active } });
    };

    const handleOpeningTimeChange = (day: string, hour: number, minute: number) => {
        if (hour !== undefined && minute !== undefined) {
            setDayTimes((prevState) => ({
                ...prevState,
                [day]: {
                    ...prevState[day],
                    opening: { hour, minute },
                },
            }));
        }
        const updatedOpeningTime = { hour: hour as number, minute: minute as number };
        onDayTimesChange({ ...dayTimes, [day]: { ...dayTimes[day], opening: updatedOpeningTime } });
    };

    const handleClosingTimeChange = (day: string, hour: number, minute: number) => {
        if (hour !== undefined && minute !== undefined) {
            setDayTimes((prevState) => ({
                ...prevState,
                [day]: {
                    ...prevState[day],
                    closing: { hour, minute },
                },
            }));
        }
        const updatedClosingTime = { hour: hour as number, minute: minute as number };
        onDayTimesChange({ ...dayTimes, [day]: { ...dayTimes[day], closing: updatedClosingTime } });
    };

    useEffect(() => {
        const initialDayTimes: LabSettings = {};

        currentLab.openingHours.forEach((hours, index) => {
            const openingDateTime = new Date(currentLab.openingHours[index].time.from);
            const closingDateTime = new Date(currentLab.openingHours[index].time.to);
            const day = hours.day;

            const dbActive = currentLab.openingHours[index].active;
            const openingRoundedMinute = Math.round(openingDateTime.getMinutes() / 5) * 5;
            const closingRoundedMinute = Math.round(closingDateTime.getMinutes() / 5) * 5;
            initialDayTimes[day] = {
                opening: {
                    hour: openingDateTime.getHours(),
                    minute: openingRoundedMinute,
                },
                closing: {
                    hour: closingDateTime.getHours(),
                    minute: closingRoundedMinute,
                },
                active: dbActive,
            };
        });

        setDayTimes(initialDayTimes);
    }, [currentLab]);

    return (
        <Fragment>
            <Grid container sx={{ marginBottom: 1 }}>
                <Grid item xs={3}>
                    <Box display="flex" alignItems="center">
                        <Typography variant="subtitle2">{translate('lab-settings:DAY')}</Typography>
                    </Box>
                </Grid>
                <Grid item xs={3}>
                    <Typography variant="subtitle2">{translate('lab-settings:OPENING')}</Typography>
                </Grid>
                <Grid item xs={3}>
                    <Typography variant="subtitle2">{translate('lab-settings:CLOSING')}</Typography>
                </Grid>
            </Grid>
            {Object.keys(dayTimes).map((key) => {
                return (
                    <Grid container key={key}>
                        <Grid item xs={3}>
                            <Box display="flex" alignItems="center">
                                <Checkbox checked={dayTimes[key].active} onChange={() => toggleDay(key)} />
                                <Box>{translate(`lab-settings:${key.toUpperCase()}`)}</Box>
                            </Box>
                        </Grid>
                        {dayTimes[key] && dayTimes[key].active && (
                            <Fragment>
                                <Grid item xs={3}>
                                    <TimeMinute
                                        onTimeChange={(hour, minute) =>
                                            handleOpeningTimeChange(key, hour as number, minute as number)
                                        }
                                        hour={dayTimes[key].opening.hour}
                                        minute={dayTimes[key].opening.minute}
                                        showFullHourMinute={true}
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <TimeMinute
                                        onTimeChange={(hour, minute) =>
                                            handleClosingTimeChange(key, hour as number, minute as number)
                                        }
                                        hour={dayTimes[key].closing.hour}
                                        minute={dayTimes[key].closing.minute}
                                        showFullHourMinute={true}
                                    />
                                </Grid>
                            </Fragment>
                        )}
                    </Grid>
                );
            })}
            <Box display="flex" justifyContent="center"></Box>
        </Fragment>
    );
};

export default LabOpeningHours;
